import type { ComplianceSectionPdfLayout } from './complianceSectionPdfLayout'
import type { SectionConfig } from './types'

interface PackagingMinimisationSectionEntry {
  section: string
  title: string
  hardcodedContent?: string
  manualCompletionInstruction?: string
  pdfLayout?: ComplianceSectionPdfLayout
}

const SECTION_1_PURPOSE = `This assessment documents the evaluation of packaging minimisation in accordance with the Packaging and Packaging Waste Regulation (PPWR), the Packaging and Packaging Waste Directive (PPWD) (94/62/EC) and EN 13428:2004. The purpose is to ensure the packaging volume and weight is limited to the minimum necessary amount to maintain the necessary level of safety, hygiene and acceptance for the packed product and for the consumer.`

const SECTION_2_1_METHODOLOGY = `This assessment evaluates whether packaging has been reduced as much as reasonably possible. It does this by looking for critical areas. A critical area is a packaging requirement that the company cannot compromise on, without causing the packaging to fail in any of the following performance criteria.

This assessment evaluates the complete list of performance criteria to determine the critical area that will govern the achievable limit for source reduction. Namely, the reason why the packaging cannot be reduced any further.

Information on each performance criterion is listed below.`

const SECTION_3_CHECKLIST = `| Performance Criterion | Most important/relevant requirement | Critical area | References |
| --- | --- | --- | --- |
| Product protection | | | |
| Packaging manufacturing processes | | | |
| Packing/filling process | | | |
| Logistics (inc. Transport, warehousing and handling) | | | |
| Product presentation and marketing | | | |
| User/consumer acceptance | | | |
| Information | | | |
| Safety | | | |
| Legislation | | | |
| Other issues | | | |`

const BASE_SECTION_CONFIGS: PackagingMinimisationSectionEntry[] = [
  {
    section: '1',
    title: 'Purpose of Assessment',
    hardcodedContent: SECTION_1_PURPOSE,
  },
  {
    section: '2.1',
    title: 'Methodology',
    hardcodedContent: SECTION_2_1_METHODOLOGY,
  },
  {
    section: '2.2',
    title: 'Assessment Outcome: Minimum Weight and Volume',
    manualCompletionInstruction:
      'Complete with minimum necessary weight and volume values.',
  },
  {
    section: '2.3.1',
    title: 'Product protection',
  },
  {
    section: '2.3.2',
    title: 'Packaging manufacturing processes',
  },
  {
    section: '2.3.3',
    title: 'Packing/filling process',
  },
  {
    section: '2.3.4',
    title: 'Logistics',
  },
  {
    section: '2.3.5',
    title: 'Product presentation and marketing',
  },
  {
    section: '2.3.6',
    title: 'User/consumer acceptance',
  },
  {
    section: '2.3.7',
    title: 'Information',
  },
  {
    section: '2.3.8',
    title: 'Safety',
  },
  {
    section: '2.3.9',
    title: 'Legislation',
  },
  {
    section: '2.3.10',
    title: 'Other issues',
    manualCompletionInstruction:
      'There may be other reasons that limit further packaging reduction. If so, explain them here.',
  },
  {
    section: '3',
    title: 'Performance criteria checklist',
    hardcodedContent: SECTION_3_CHECKLIST,
    pdfLayout: 'landscape-page-start',
  },
]

export const PACKAGING_MINIMISATION_SECTION_IDS: readonly string[] =
  BASE_SECTION_CONFIGS.map((entry) => entry.section)

export function getPackagingMinimisationSectionConfigs(): SectionConfig[] {
  return BASE_SECTION_CONFIGS.map((entry) => ({ ...entry }))
}

const PACKAGING_MINIMISATION_PDF_LAYOUT_BY_ID = new Map<
  string,
  ComplianceSectionPdfLayout
>(
  BASE_SECTION_CONFIGS.flatMap((entry) =>
    entry.pdfLayout != null ? [[entry.section, entry.pdfLayout] as const] : []
  )
)

export function getPackagingMinimisationAssessmentSectionPdfLayout(
  sectionId: string
): ComplianceSectionPdfLayout {
  return (
    PACKAGING_MINIMISATION_PDF_LAYOUT_BY_ID.get(sectionId) ??
    'portrait-continuous'
  )
}

export function resolvePackagingMinimisationAssessmentSectionPdfLayout(
  sectionId: string
): ComplianceSectionPdfLayout {
  if (/^appendix-/i.test(sectionId)) {
    return 'portrait-page-start'
  }

  return getPackagingMinimisationAssessmentSectionPdfLayout(sectionId)
}
